// Protein run rate — the trailing seven days ending on the VIEWED day.
//
// Pure functions only, same contract as weekWindow.js: Food.js hands in rows
// already grouped by day; nothing here fetches anything.
//
// THE DENOMINATOR RULE. An unlogged day is NOT a zero-protein day — the
// em-dash rule from the nutrient grain, moved to the day grain. The rate
// divides by the days that were logged, never by the window width: 174g over
// two logged days is 87 g/day, and dividing by seven would report 25 g/day —
// a false and alarming number manufactured from days that said nothing.
//
// What counts as a logged day is weekWindow's classifyDay, not a second test
// written here. Two implementations of "logged" could silently disagree, and
// then this card and the Past 7 days overlay would count the same window
// differently.

import { totalNutrients } from './foodLog';
import { classifyDay } from './weekWindow';

// The target now lives in nutritionTargets.js with every other target;
// re-exported so existing importers (Food.js) keep working unchanged.
export { PROTEIN_TARGET_G_PER_DAY } from './nutritionTargets';

export const PROTEIN_WINDOW_DAYS = 7;

// { runRate, loggedDays, windowDays, undisclosed } — never a bare number.
//
//   runRate      g/day over the logged days, or null when there are none.
//   loggedDays   days in the window with at least one Food_Log row.
//   windowDays   the window width the caller asked about, restated so the
//                "N of 7" caveat and the arithmetic can never disagree.
//   undisclosed  logged rows that withheld protein. When > 0 the rate is a
//                floor — those rows are in the day count but contributed
//                nothing to the sum — and the card marks it with the same
//                superscript plus every other partial total uses.
export function proteinRunRate({ dayKeys, rowsByDay }) {
  let proteinTotal = 0;
  let loggedDays = 0;
  let undisclosed = 0;

  for (const dayKey of dayKeys) {
    const rows = rowsByDay[dayKey] ?? [];
    if (classifyDay(rows).state === 'unlogged') continue;
    loggedDays += 1;
    const protein = totalNutrients(rows).Protein;
    if (protein.value !== null) proteinTotal += protein.value;
    undisclosed += protein.undisclosed;
  }

  return {
    runRate: loggedDays === 0 ? null : proteinTotal / loggedDays,
    loggedDays,
    windowDays: PROTEIN_WINDOW_DAYS,
    undisclosed,
  };
}

// The same reducer for any Food_Log nutrient column, same shape out, same
// rules throughout: the denominator is loggedDays and never windowDays, an
// unlogged day contributes nothing anywhere, and withheld rows accumulate
// into `undisclosed` so the caller can mark the rate as a floor.
//
// The key is passed to totalNutrients EXPLICITLY as [key]: its default key
// set is NUTRIENTS, which excludes the two mcg columns (IodineMcg,
// RetinolMcg), and reading a key the reducer was not asked to total would be
// undefined, not a rate.
export function nutrientRunRate({ dayKeys, rowsByDay, key }) {
  let total = 0;
  let loggedDays = 0;
  let undisclosed = 0;

  for (const dayKey of dayKeys) {
    const rows = rowsByDay[dayKey] ?? [];
    if (classifyDay(rows).state === 'unlogged') continue;
    loggedDays += 1;
    const reading = totalNutrients(rows, [key])[key];
    if (reading.value !== null) total += reading.value;
    undisclosed += reading.undisclosed;
  }

  return {
    runRate: loggedDays === 0 ? null : total / loggedDays,
    loggedDays,
    windowDays: PROTEIN_WINDOW_DAYS,
    undisclosed,
  };
}
